/**
 * 底部工具栏组件 - 分类标签页版本
 */
import React, { useState, useRef } from 'react';
import { useReactFlow, addEdge } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useNodeOperations } from './providers/NodeOperationsProvider';
import LogService from '../../api/LogService';
import './Toolbar.css';

// 按功能分类的节点配置
const toolCategories = {
  input: {
    label: '输入节点',
    icon: '📥',
    tools: [
      { id: 'promptInput', label: '文本输入', icon: '📝', description: '输入提示词' },
      { id: 'imageReference', label: '图片参考', icon: '🖼️', description: '上传参考图' },
      { id: 'videoReference', label: '视频参考', icon: '🎞️', description: '上传参考视频' },
      { id: 'audioReference', label: '音频参考', icon: '🎵', description: '上传参考音频' },
      { id: 'drawing', label: '手绘画板', icon: '✏️', description: '手绘草图' },
    ]
  },
  image: {
    label: '图像模型',
    icon: '🎨',
    tools: [
      { id: 'gptImage2', label: 'GPT 2 图像生成', icon: '✨', description: '高精度生成与参考图编辑，最高 4K' },
      { id: 'nanoBanana2', label: 'Nano 2 图像生成', icon: '🍌', description: '快速生图与多图参考，最高 4K' },
    ]
  },
  video: {
    label: '视频模型',
    icon: '🎬',
    tools: [
      { id: 'minimaxH3Video', label: '海螺 H3 视频生成', icon: '🎥', description: '支持图片与视频参考，768P/2K' },
      { id: 'klingO3ReferenceVideo', label: '可灵 O3 视频生成', icon: '🎞️', description: '图片与视频参考，合计最多4个' },
      { id: 'geminiOmniFlashVideo', label: 'Gemini 极速视频生成', icon: '⚡', description: '快速多参考视频，最多5张图片' },
      { id: 'superSeed2LiteVideo', label: 'Seedance 2.5 视频生成', icon: '🎬', description: '全能参考：30图、10视频、10音频' },
    ]
  },
  audio: {
    label: '音频模型',
    icon: '🎵',
    tools: [
      { id: 'textToAudio', label: '语音合成', icon: '🔊', description: '文字转语音' },
      { id: 'hailuoMusic', label: '音乐生成', icon: '🎶', description: '海螺音乐生成' },
      { id: 'sunoMusic', label: 'Suno 音乐生成', icon: '🎤', description: 'Suno AI音乐生成' },
    ]
  },
  tools: {
    label: '工具箱',
    icon: '🔧',
    tools: [
      { id: 'note', label: '便签', icon: '📌', description: '添加说明标签' },
      { id: 'audioTrim', label: '音频剪辑', icon: '✂️', description: '剪辑音频片段' },
    ]
  },
  // effect: {
  //   label: '特效',
  //   icon: '✨',
  //   tools: []
  // },
};

export const Toolbar = () => {
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const hoverTimeoutRef = useRef(null);
  const { addNode } = useNodeOperations();
  const { addNodes, setEdges, screenToFlowPosition } = useReactFlow();

  const handleAddNode = (nodeId, nodeData) => {
    // 记录节点添加日志
    const nodeName = toolCategories[Object.keys(toolCategories).find(catKey => 
      toolCategories[catKey].tools?.some(tool => tool.id === nodeId)
    )]?.tools?.find(tool => tool.id === nodeId)?.label || nodeId;
    
    LogService.logButtonClick(`添加节点-${nodeName}`, `add-node-${nodeId}`, {
      nodeType: nodeId,
      nodeName: nodeName,
      actionType: '添加画布节点',
      pageName: '画布编辑器'
    });
    
    // 新版图片模型统一创建提示词和可选参考图输入节点。
    if (nodeId === 'gptImage2' || nodeId === 'nanoBanana2') {
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });
      const promptNodeId = nanoid();
      const imageRefNodeId = nanoid();
      const modelNodeId = nanoid();

      addNodes([
        {
          id: promptNodeId,
          type: 'promptInput',
          position: { x: centerPosition.x - 450, y: centerPosition.y - 110 },
          data: { prompt: '' },
        },
        {
          id: imageRefNodeId,
          type: 'imageReference',
          position: { x: centerPosition.x - 450, y: centerPosition.y + 150 },
          data: { imageUrl: '' },
        },
        {
          id: modelNodeId,
          type: nodeId,
          position: centerPosition,
          data: nodeData || {},
        },
      ]);

      setEdges(eds => {
        const withPrompt = addEdge({
          id: nanoid(),
          source: promptNodeId,
          target: modelNodeId,
          targetHandle: 'prompt',
          type: 'default',
        }, eds);
        return addEdge({
          id: nanoid(),
          source: imageRefNodeId,
          target: modelNodeId,
          targetHandle: 'image',
          type: 'default',
        }, withPrompt);
      });
    } else if (['minimaxH3Video', 'klingO3ReferenceVideo', 'geminiOmniFlashVideo', 'superSeed2LiteVideo'].includes(nodeId)) {
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });
      const promptNodeId = nanoid();
      const imageRefNodeId = nanoid();
      const modelNodeId = nanoid();

      addNodes([
        {
          id: promptNodeId,
          type: 'promptInput',
          position: { x: centerPosition.x - 460, y: centerPosition.y - 115 },
          data: { prompt: '' },
        },
        {
          id: imageRefNodeId,
          type: 'imageReference',
          position: { x: centerPosition.x - 460, y: centerPosition.y + 155 },
          data: { imageUrl: '' },
        },
        {
          id: modelNodeId,
          type: nodeId,
          position: centerPosition,
          data: nodeData || {},
        },
      ]);

      setEdges(eds => {
        const withPrompt = addEdge({
          id: nanoid(),
          source: promptNodeId,
          target: modelNodeId,
          targetHandle: 'prompt',
          type: 'default',
        }, eds);
        return addEdge({
          id: nanoid(),
          source: imageRefNodeId,
          target: modelNodeId,
          targetHandle: 'image',
          type: 'default',
        }, withPrompt);
      });
    // 如果是文生图节点，自动创建文本输入节点并连接
    } else if (nodeId === 'textToImage') {
      // 获取视口中心位置
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      // 创建文本输入节点（在文生图节点左侧）
      const promptNodePosition = {
        x: centerPosition.x - 450, // 在左侧450px处
        y: centerPosition.y,
      };

      // 创建文生图节点（在中间）
      const textToImagePosition = centerPosition;

      // 生成节点ID
      const promptNodeId = nanoid();
      const textToImageNodeId = nanoid();

      // 创建节点对象
      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const textToImageNode = {
        id: textToImageNodeId,
        type: nodeId,
        position: textToImagePosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, textToImageNode]);

      // 立即创建连接边
      const newEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: textToImageNodeId,
        type: 'default',
      };
      setEdges((eds) => addEdge(newEdge, eds));
    } else if (nodeId === 'textToAudio') {
      // 如果是语音合成节点，自动创建文本输入节点并连接
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      // 创建文本输入节点（在语音合成节点左侧）
      const promptNodePosition = {
        x: centerPosition.x - 450, // 在左侧450px处
        y: centerPosition.y,
      };

      // 创建语音合成节点（在中间）
      const textToAudioPosition = centerPosition;

      // 生成节点ID
      const promptNodeId = nanoid();
      const textToAudioNodeId = nanoid();

      // 创建节点对象
      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const textToAudioNode = {
        id: textToAudioNodeId,
        type: nodeId,
        position: textToAudioPosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, textToAudioNode]);

      // 立即创建连接边
      const newEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: textToAudioNodeId,
        targetHandle: 'prompt',
        type: 'default',
      };
      setEdges((eds) => addEdge(newEdge, eds));
    } else if (nodeId === 'hailuoMusic') {
      // 如果是音乐生成节点，自动创建两个文本输入节点并连接
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      // 创建音乐描述文本输入节点（在左上侧）
      const promptNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y - 80,
      };

      // 创建歌词文本输入节点（在左下侧）
      const lyricsNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y + 80,
      };

      const promptNodeId = nanoid();
      const lyricsNodeId = nanoid();
      const musicNodeId = nanoid();

      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const lyricsNode = {
        id: lyricsNodeId,
        type: 'promptInput',
        position: lyricsNodePosition,
        data: { prompt: '' },
      };

      const musicNode = {
        id: musicNodeId,
        type: nodeId,
        position: centerPosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, lyricsNode, musicNode]);

      // 创建连接边：音乐描述 -> 音乐节点（上方 Handle）
      const promptEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: musicNodeId,
        targetHandle: 'prompt',
        type: 'default',
      };

      // 创建连接边：歌词 -> 音乐节点（下方 Handle）
      const lyricsEdge = {
        id: nanoid(),
        source: lyricsNodeId,
        target: musicNodeId,
        targetHandle: 'lyrics',
        type: 'default',
      };

      setEdges((eds) => {
        const withPromptEdge = addEdge(promptEdge, eds);
        return addEdge(lyricsEdge, withPromptEdge);
      });
    } else if (nodeId === 'sunoMusic') {
      // 如果是 Suno 音乐生成节点，自动创建一个文本输入节点（歌词）并连接
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      // 创建歌词文本输入节点（在左侧）
      const lyricsNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y,
      };

      const lyricsNodeId = nanoid();
      const sunoNodeId = nanoid();

      const lyricsNode = {
        id: lyricsNodeId,
        type: 'promptInput',
        position: lyricsNodePosition,
        data: { prompt: '' },
      };

      const sunoNode = {
        id: sunoNodeId,
        type: nodeId,
        position: centerPosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([lyricsNode, sunoNode]);

      // 创建连接边：歌词 -> Suno 音乐节点
      const lyricsEdge = {
        id: nanoid(),
        source: lyricsNodeId,
        target: sunoNodeId,
        targetHandle: 'lyrics',
        type: 'default',
      };

      setEdges((eds) => addEdge(lyricsEdge, eds));
    } else if (nodeId === 'minimaxImage') {
      // 如果是海螺画图节点，自动创建文本输入节点并连接
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      // 创建文本输入节点（在海螺画图节点左侧）
      const promptNodePosition = {
        x: centerPosition.x - 450, // 在左侧450px处
        y: centerPosition.y,
      };

      // 创建海螺画图节点（在中间）
      const minimaxImagePosition = centerPosition;

      // 生成节点ID
      const promptNodeId = nanoid();
      const minimaxImageNodeId = nanoid();

      // 创建节点对象
      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const minimaxImageNode = {
        id: minimaxImageNodeId,
        type: nodeId,
        position: minimaxImagePosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, minimaxImageNode]);

      // 立即创建连接边
      const newEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: minimaxImageNodeId,
        targetHandle: 'prompt',
        type: 'default',
      };
      setEdges((eds) => addEdge(newEdge, eds));
    } else if (nodeId === 'googleBanana') {
      // 如果是谷歌nano banana节点，自动创建文本输入节点并连接
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      // 创建文本输入节点（在谷歌nano banana节点左侧）
      const promptNodePosition = {
        x: centerPosition.x - 450, // 在左侧450px处
        y: centerPosition.y,
      };

      // 创建谷歌nano banana节点（在中间）
      const googleBananaPosition = centerPosition;

      // 生成节点ID
      const promptNodeId = nanoid();
      const googleBananaNodeId = nanoid();

      // 创建节点对象
      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const googleBananaNode = {
        id: googleBananaNodeId,
        type: nodeId,
        position: googleBananaPosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, googleBananaNode]);

      // 立即创建连接边
      const newEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: googleBananaNodeId,
        targetHandle: 'prompt',
        type: 'default',
      };
      setEdges((eds) => addEdge(newEdge, eds));
    } else if (nodeId === 'bananaPro') {
      // 如果是 Banana Pro 节点，自动创建文本输入节点并连接
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      // 创建文本输入节点（在 Banana Pro 节点左侧）
      const promptNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y,
      };

      const promptNodeId = nanoid();
      const bananaProNodeId = nanoid();

      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const bananaProNode = {
        id: bananaProNodeId,
        type: nodeId,
        position: centerPosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, bananaProNode]);

      // 创建连接边
      const newEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: bananaProNodeId,
        targetHandle: 'prompt',
        type: 'default',
      };
      setEdges((eds) => addEdge(newEdge, eds));
    } else if (nodeId === 'referenceToImage') {
      // 如果是参考生图节点，自动创建文本输入节点和图片参考节点并连接
      // 获取视口中心位置
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      // 创建文本输入节点（在参考生图节点左上方）
      const promptNodePosition = {
        x: centerPosition.x - 450, // 在左侧450px处
        y: centerPosition.y - 100, // 在上方100px处
      };

      // 创建图片参考节点（在参考生图节点左下方）
      const imageRefNodePosition = {
        x: centerPosition.x - 450, // 在左侧450px处
        y: centerPosition.y + 150, // 在下方150px处
      };

      // 创建参考生图节点（在中间）
      const referenceToImagePosition = centerPosition;

      // 生成节点ID
      const promptNodeId = nanoid();
      const imageRefNodeId = nanoid();
      const referenceToImageNodeId = nanoid();

      // 创建节点对象
      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const imageRefNode = {
        id: imageRefNodeId,
        type: 'imageReference',
        position: imageRefNodePosition,
        data: { imageUrl: '' },
      };

      const referenceToImageNode = {
        id: referenceToImageNodeId,
        type: nodeId,
        position: referenceToImagePosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, imageRefNode, referenceToImageNode]);

      // 创建连接边：文本输入 -> 参考生图（连接到文本输入点）
      const promptEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: referenceToImageNodeId,
        targetHandle: 'prompt',
        type: 'default',
      };
      
      // 创建连接边：图片参考 -> 参考生图（连接到图片输入点）
      const imageEdge = {
        id: nanoid(),
        source: imageRefNodeId,
        target: referenceToImageNodeId,
        targetHandle: 'image',
        type: 'default',
      };

      setEdges((eds) => {
        const withPromptEdge = addEdge(promptEdge, eds);
        return addEdge(imageEdge, withPromptEdge);
      });
    } else if (nodeId === 'imengImageGen') {
      // 如果是即梦生图节点，自动创建文本输入节点和图片参考节点并连接
      // 获取视口中心位置
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      const promptNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y - 120,
      };

      const imageRefNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y + 160,
      };

      const imengImageGenPosition = centerPosition;

      // 生成节点ID
      const promptNodeId = nanoid();
      const imageRefNodeId = nanoid();
      const imengImageGenNodeId = nanoid();

      // 创建节点对象
      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const imageRefNode = {
        id: imageRefNodeId,
        type: 'imageReference',
        position: imageRefNodePosition,
        data: { imageUrl: '' },
      };

      const imengImageGenNode = {
        id: imengImageGenNodeId,
        type: nodeId,
        position: imengImageGenPosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, imageRefNode, imengImageGenNode]);

      // 创建连接边：文本输入 -> 即梦生图（连接到文本输入点）
      const promptEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: imengImageGenNodeId,
        targetHandle: 'prompt',
        type: 'default',
      };

      // 创建连接边：图片参考 -> 即梦生图（连接到图片输入点）
      const imageEdge = {
        id: nanoid(),
        source: imageRefNodeId,
        target: imengImageGenNodeId,
        targetHandle: 'image',
        type: 'default',
      };

      setEdges((eds) => {
        const withPromptEdge = addEdge(promptEdge, eds);
        return addEdge(imageEdge, withPromptEdge);
      });
    } else if (nodeId === 'sora2Video' || nodeId === 'veo3Video') {
      // 如果是视频生成节点（SORA2/VEO3），自动创建文本输入和图片参考节点
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      const promptNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y - 120,
      };

      const imageRefNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y + 160,
      };

      const videoNodePosition = centerPosition;

      // 生成节点ID
      const promptNodeId = nanoid();
      const imageRefNodeId = nanoid();
      const videoNodeId = nanoid();

      // 创建节点对象
      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const imageRefNode = {
        id: imageRefNodeId,
        type: 'imageReference',
        position: imageRefNodePosition,
        data: { imageUrl: '' },
      };

      const videoNode = {
        id: videoNodeId,
        type: nodeId,
        position: videoNodePosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, imageRefNode, videoNode]);

      // 创建连接边：文本输入 -> 视频节点（连接到文本输入点）
      const promptEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: videoNodeId,
        targetHandle: 'prompt',
        type: 'default',
      };

      // 创建连接边：图片参考 -> 视频节点（连接到图片输入点）
      const imageEdge = {
        id: nanoid(),
        source: imageRefNodeId,
        target: videoNodeId,
        targetHandle: 'image',
        type: 'default',
      };

      setEdges((eds) => {
        const withPromptEdge = addEdge(promptEdge, eds);
        return addEdge(imageEdge, withPromptEdge);
      });
    } else if (nodeId === 'doubaoVideo') {
      // 如果是豆包视频节点，自动创建文本输入和图片参考节点，并连接到指定的 handle
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      const promptNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y - 120,
      };

      const imageRefNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y + 160,
      };

      const videoNodePosition = centerPosition;

      // 生成节点ID
      const promptNodeId = nanoid();
      const imageRefNodeId = nanoid();
      const videoNodeId = nanoid();

      // 创建节点对象
      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const imageRefNode = {
        id: imageRefNodeId,
        type: 'imageReference',
        position: imageRefNodePosition,
        data: { imageUrl: '' },
      };

      const videoNode = {
        id: videoNodeId,
        type: nodeId,
        position: videoNodePosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, imageRefNode, videoNode]);

      // 创建连接边，指定 targetHandle
      const promptEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: videoNodeId,
        targetHandle: 'prompt', // 连接到文本输入 handle
        type: 'default',
      };

      const imageEdge = {
        id: nanoid(),
        source: imageRefNodeId,
        target: videoNodeId,
        targetHandle: 'image', // 连接到图片输入 handle
        type: 'default',
      };

      setEdges((eds) => {
        const withPromptEdge = addEdge(promptEdge, eds);
        return addEdge(imageEdge, withPromptEdge);
      });
    } else if (nodeId === 'hailuo23Video') {
      // 如果是海螺2.3视频节点，自动创建文本输入和图片参考节点
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      const promptNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y - 120,
      };

      const imageRefNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y + 160,
      };

      const videoNodePosition = centerPosition;

      // 生成节点ID
      const promptNodeId = nanoid();
      const imageRefNodeId = nanoid();
      const videoNodeId = nanoid();

      // 创建节点对象
      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const imageRefNode = {
        id: imageRefNodeId,
        type: 'imageReference',
        position: imageRefNodePosition,
        data: { imageUrl: '' },
      };

      const videoNode = {
        id: videoNodeId,
        type: nodeId,
        position: videoNodePosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, imageRefNode, videoNode]);

      // 创建连接边，指定 targetHandle
      const promptEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: videoNodeId,
        targetHandle: 'prompt', // 连接到文本输入 handle
        type: 'default',
      };

      const imageEdge = {
        id: nanoid(),
        source: imageRefNodeId,
        target: videoNodeId,
        targetHandle: 'image', // 连接到图片输入 handle
        type: 'default',
      };

      setEdges((eds) => {
        const withPromptEdge = addEdge(promptEdge, eds);
        return addEdge(imageEdge, withPromptEdge);
      });
    } else if (nodeId === 'klingVideo') {
      // 如果是可灵2.5视频节点，自动创建文本输入和图片参考节点
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      const promptNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y - 120,
      };

      const imageRefNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y + 160,
      };

      const videoNodePosition = centerPosition;

      // 生成节点ID
      const promptNodeId = nanoid();
      const imageRefNodeId = nanoid();
      const videoNodeId = nanoid();

      // 创建节点对象
      const promptNode = {
        id: promptNodeId,
        type: 'promptInput',
        position: promptNodePosition,
        data: { prompt: '' },
      };

      const imageRefNode = {
        id: imageRefNodeId,
        type: 'imageReference',
        position: imageRefNodePosition,
        data: { imageUrl: '' },
      };

      const videoNode = {
        id: videoNodeId,
        type: nodeId,
        position: videoNodePosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([promptNode, imageRefNode, videoNode]);

      // 创建连接边，指定 targetHandle
      const promptEdge = {
        id: nanoid(),
        source: promptNodeId,
        target: videoNodeId,
        targetHandle: 'prompt', // 连接到文本输入 handle
        type: 'default',
      };

      const imageEdge = {
        id: nanoid(),
        source: imageRefNodeId,
        target: videoNodeId,
        targetHandle: 'image', // 连接到图片输入 handle
        type: 'default',
      };

      setEdges((eds) => {
        const withPromptEdge = addEdge(promptEdge, eds);
        return addEdge(imageEdge, withPromptEdge);
      });
    } else if (nodeId === 'videoFaceSwap') {
      // 如果是视频换脸节点，自动创建图片参考节点（人脸）和视频参考节点（源视频）
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      const faceNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y - 120,
      };

      const videoRefNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y + 160,
      };

      const faceSwapNodePosition = centerPosition;

      // 生成节点ID
      const faceNodeId = nanoid();
      const videoRefNodeId = nanoid();
      const faceSwapNodeId = nanoid();

      // 创建节点对象
      const faceNode = {
        id: faceNodeId,
        type: 'imageReference',
        position: faceNodePosition,
        data: { imageUrl: '' },
      };

      const videoRefNode = {
        id: videoRefNodeId,
        type: 'videoReference',
        position: videoRefNodePosition,
        data: { videoUrl: '' },
      };

      const faceSwapNode = {
        id: faceSwapNodeId,
        type: nodeId,
        position: faceSwapNodePosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([faceNode, videoRefNode, faceSwapNode]);

      // 创建连接边，指定 targetHandle
      const faceEdge = {
        id: nanoid(),
        source: faceNodeId,
        target: faceSwapNodeId,
        targetHandle: 'face', // 连接到人脸输入 handle
        type: 'default',
      };

      const videoEdge = {
        id: nanoid(),
        source: videoRefNodeId,
        target: faceSwapNodeId,
        targetHandle: 'video', // 连接到视频输入 handle
        type: 'default',
      };

      setEdges((eds) => {
        const withFaceEdge = addEdge(faceEdge, eds);
        return addEdge(videoEdge, withFaceEdge);
      });
    } else if (nodeId === 'klingLipSync') {
      // 如果是可灵对口型节点，自动创建视频参考节点和音频参考节点
      const centerPosition = screenToFlowPosition({
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      });

      const videoRefNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y - 120,
      };

      const audioRefNodePosition = {
        x: centerPosition.x - 450,
        y: centerPosition.y + 120,
      };

      const lipSyncNodePosition = centerPosition;

      // 生成节点ID
      const videoRefNodeId = nanoid();
      const audioRefNodeId = nanoid();
      const lipSyncNodeId = nanoid();

      // 创建节点对象
      const videoRefNode = {
        id: videoRefNodeId,
        type: 'videoReference',
        position: videoRefNodePosition,
        data: { videoUrl: '' },
      };

      const audioRefNode = {
        id: audioRefNodeId,
        type: 'audioReference',
        position: audioRefNodePosition,
        data: { audioUrl: '' },
      };

      const lipSyncNode = {
        id: lipSyncNodeId,
        type: nodeId,
        position: lipSyncNodePosition,
        data: nodeData || {},
      };

      // 一次性添加所有节点
      addNodes([videoRefNode, audioRefNode, lipSyncNode]);

      // 创建连接边，指定 targetHandle
      const videoEdge = {
        id: nanoid(),
        source: videoRefNodeId,
        target: lipSyncNodeId,
        targetHandle: 'video', // 连接到视频输入 handle
        type: 'default',
      };

      const audioEdge = {
        id: nanoid(),
        source: audioRefNodeId,
        target: lipSyncNodeId,
        targetHandle: 'audio', // 连接到音频输入 handle
        type: 'default',
      };

      setEdges((eds) => {
        const withVideoEdge = addEdge(videoEdge, eds);
        return addEdge(audioEdge, withVideoEdge);
      });
    } else {
      // 其他节点正常创建
      addNode(nodeId, { data: nodeData });
    }
  };

  // 开始拖动
  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  // 处理鼠标进入 - 清除任何待定的关闭定时器
  const handleMouseEnter = (categoryKey) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setHoveredCategory(categoryKey);
  };

  // 处理鼠标离开 - 使用延迟确保移到面板时不会关闭
  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredCategory(null);
      hoverTimeoutRef.current = null;
    }, 150);
  };

  return (
    <div className="Toolbar-wrapper canvas-toolbar">
      {/* 底部分类菜单栏 */}
      <div className="Toolbar-container">
        {Object.entries(toolCategories).map(([categoryKey, category]) => {
          const isHovered = hoveredCategory === categoryKey;
          const tools = category.tools || [];
          
          return (
            <div 
              key={categoryKey}
              className="Toolbar-category-item"
              onMouseEnter={() => handleMouseEnter(categoryKey)}
              onMouseLeave={handleMouseLeave}
            >
              {/* 工具弹出面板 */}
              {isHovered && tools.length > 0 && (
                <div 
                  className="Toolbar-popup"
                  onMouseEnter={() => handleMouseEnter(categoryKey)}
                  onMouseLeave={handleMouseLeave}
                >
                  {tools.map((tool) => (
                    <button
                      key={tool.id}
                      onClick={() => {
                        handleAddNode(tool.id, tool.data);
                        setHoveredCategory(null); // 点击后关闭面板
                      }}
                      onDragStart={(event) => onDragStart(event, tool.id)}
                      draggable
                      title={`${tool.label}：${tool.description}（可拖动到画布）`}
                      className="Toolbar-button"
                    >
                      <span className="Toolbar-icon">{tool.icon}</span>
                      <span className="Toolbar-label">{tool.label}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {/* 分类按钮 */}
              <button
                className={`Toolbar-category-tab ${isHovered ? 'Toolbar-category-tab-active' : ''}`}
                title={category.label}
              >
                <span className="Toolbar-category-icon">{category.icon}</span>
                <span className="Toolbar-category-label">{category.label}</span>
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
